<template>
    <div>
        <v-dialog v-model="isOpen" max-width="1000px" scrollable class="form-wrapper">
            <v-card class="user-suggestion-modal-card" height="90vh">
                <!-- Header -->
                <v-card-title class="pa-4" style="background: white; color: black;">
                    <h4 style="color: black;">{{ $t('user_suggestion_form.title') }}</h4>
                    <v-btn icon @click="close">
                        <v-icon class="close-icon" style="color: black;">mdi-close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-divider color="lightgrey"></v-divider>

                <!-- Suggestion Form -->
                <v-card-text class="pa-4">
                    <div class="mb-4">
                        <label class="black-text">{{ $t('user_suggestion_form.suggested_assessment') }}</label>
                        <v-text-field
                            v-model="form.suggestion"
                            :placeholder="$t('user_suggestion_form.suggestion_placeholder')"
                            outlined
                            dense
                            hide-details
                            required
                            class="mt-2 black-text bordered-input"
                        />
                    </div>
                    <div class="mb-4">
                        <label class="black-text">{{ $t('user_suggestion_form.your_name') }}</label>
                        <v-text-field
                            v-model="form.name"
                            :placeholder="$t('user_suggestion_form.name_placeholder')"
                            outlined
                            dense
                            hide-details
                            required
                            class="mt-2 black-text bordered-input"
                        />
                    </div>
                    <div class="mb-4">
                        <label class="black-text">{{ $t('user_suggestion_form.may_contact') }}</label>
                        <div style="display: flex; gap: 16px; margin-top: 8px;">
                            <label style="display: flex; align-items: center;" class="black-text">
                                <input type="radio" v-model="form.canContact" :value="true" required style="margin-right: 6px;" />
                                {{ $t('user_suggestion_form.yes') }}
                            </label>
                            <label style="display: flex; align-items: center;" class="black-text">
                                <input type="radio" v-model="form.canContact" :value="false" required style="margin-right: 6px;" />
                                {{ $t('user_suggestion_form.no') }}
                            </label>
                        </div>
                    </div>
                    <div v-if="form.canContact" class="mb-4">
                        <label class="black-text">{{ $t('user_suggestion_form.contact_info') }}</label>
                        <v-text-field
                            v-model="form.contact"
                            :placeholder="$t('user_suggestion_form.contact_placeholder')"
                            outlined
                            dense
                            hide-details
                            required
                            class="mt-2 black-text bordered-input"
                            :error-messages="contactError"
                            @input="validateContact"
                        />
                    </div>
                    <div class="mt-4 mb-4">
                        <label class="black-text font-weight-medium">{{ $t('user_suggestion_form.reason') }}</label>
                        <v-textarea
                            v-model="form.reason"
                            :placeholder="$t('user_suggestion_form.reason_placeholder')"
                            outlined
                            dense
                            hide-details
                            rows="2"
                            class="mt-2 black-text bordered-input"
                        ></v-textarea>
                    </div>
                    <div class="mb-2">
                        <label class="black-text font-weight-medium">{{ $t('user_suggestion_form.frequency') }}</label>
                        <select v-model="form.frequency" required class="mt-2 black-text bordered-input" style="color: black; padding: 8px; width: 100%;">
                            <option disabled value="">{{ $t('user_suggestion_form.frequency_select') }}</option>
                            <option v-for="option in frequencyOptions" :key="option.value" :value="option.value">
                                {{ $t(option.text) }}
                            </option>
                        </select>
                    </div>
                </v-card-text>

                <v-divider color="lightgrey"></v-divider>

                <!-- Footer -->
                <v-card-actions class="pa-4 grey lighten-3 justify-center" style="display: flex; gap: 16px;">
                    <v-btn
                        text
                        color="primary"
                        @click="close"
                        style="border: 1px solid lightgrey; background: white; flex: 1; min-width: 0;"
                    >
                        {{ $t('user_suggestion_form.cancel') }}
                    </v-btn>
                    <v-btn
                        class="custom-disabled-btn submit-btn-layout"
                        :class="submitBtnStateClass"
                        dark
                        @click="submitSuggestion"
                        :loading="loading"
                        :disabled="loading"
                    >
                        <v-icon color="white" left v-if="!loading" class="mr-0 pr-0">{{ icons.mdiSendVariantOutline }}</v-icon>
                        <span style="color: white;" v-if="!loading">{{ $t('user_suggestion_form.submit') }}</span>
                        <span v-else style="color: white;">{{ $t('user_suggestion_form.submitting') }}</span>
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-snackbar
            v-model="snackbar"
            :color="snackbarColor"
            bottom
            timeout="3500"
            elevation="6"
            class="snackbar-margin"
        >
            <span style="color: white;">{{ snackbarMessage }}</span>
        </v-snackbar>
    </div>
</template>

<script>
import icons from '../../mixins/icons';
import { validateContact, validateForm } from '../../utils/userSuggestionFormUtils';

export default {
    name: 'UserSuggestionForm',
    mixins: [icons],
    props: {
        value: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            form: {
                suggestion: '',
                name: '',
                contact: '',
                reason: '',
                frequency: '',
                contactMethod: '',
                canContact: false
            },
            contactError: '',
            frequencyOptions: [
                { text: 'user_suggestion_form.frequency_daily', value: 'daily' },
                { text: 'user_suggestion_form.frequency_weekly', value: 'weekly' },
                { text: 'user_suggestion_form.frequency_monthly', value: 'monthly' },
                { text: 'user_suggestion_form.frequency_rarely', value: 'rarely' },
                { text: 'user_suggestion_form.frequency_unsure', value: 'unsure' }
            ],
            contactMethodOptions: [
                { text: 'user_suggestion_form.contact_method_email', value: 'email' },
                { text: 'user_suggestion_form.contact_method_phone', value: 'phone' },
                { text: 'user_suggestion_form.contact_method_whatsapp', value: 'whatsapp' },
            ],
            loading: false,
            snackbar: false,
            snackbarMessage: '',
            snackbarColor: 'green',
        }
    },
    computed: {
        isOpen: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            }
        },
        submitBtnStateClass() {
            return this.loading ? 'submit-btn-loading' : 'submit-btn-normal';
        }
    },
    methods: {
        close() {
            this.isOpen = false
        },
        validateContact() {
            const errorKey = validateContact(this.form.contact);
            if (errorKey) {
                this.contactError = this.$t(`user_suggestion_form.${errorKey}`);
            } else {
                this.contactError = '';
            }
        },
        validateForm() {
            const result = validateForm(this.form);
            if (!result.valid) {
                return { valid: false, message: this.$t(`user_suggestion_form.${result.message}`) };
            }
            return { valid: true };
        },
        async submitSuggestion() {
            const validation = this.validateForm();
            if (!validation.valid) {
                this.snackbarMessage = validation.message;
                this.snackbarColor = 'red';
                this.snackbar = true;
                return;
            }
            this.loading = true;
            try {
                const content = {
                    name: 'user_suggestion',
                    device: {
                        engagementCode: this.$store.state.settings?.code,
                        language: this.$store.state.v4?.language?.selected,
                        hrId: this.$store.state.v4?.registration?.hrId,
                        timestamp: new Date().toISOString(),
                    },
                    data: this.form
                };
                const options = {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(content)
                };
                const response = await fetch('/api/v3/feedback', options);
                if (response.ok) {
                    this.snackbarMessage = this.$t('user_suggestion_form.submit_success');
                    this.snackbarColor = 'green';
                    this.snackbar = true;
                    this.close();
                    this.form = {
                        suggestion: '',
                        name: '',
                        contact: '',
                        reason: '',
                        frequency: '',
                        contactMethod: '',
                        canContact: false
                    };
                    this.contactError = '';
                } else {
                    this.snackbarMessage = this.$t('user_suggestion_form.submit_fail');
                    this.snackbarColor = 'red';
                    this.snackbar = true;
                }
            } catch (e) {
                this.snackbarMessage = this.$t('user_suggestion_form.submit_network_error');
                this.snackbarColor = 'red';
                this.snackbar = true;
                console.log(e);
                this.$sentry.captureException(e);
            } finally {
                this.loading = false;
            }
        }
    }
}
</script>

<style>
.black-text {
    color: black;
    font-size: 1.15rem;
}

.user-suggestion-modal-card {
    background: white;
}

.user-suggestion-modal-card .v-card__text {
    background: white;
}

.bordered-input {
    border-color: #bdbdbd !important;
    border-width: 1px !important;
    border-style: solid !important;
    border-radius: 4px !important;
    background: white !important;
}

/* Black text for labels and inputs */
.user-suggestion-modal-card,
.user-suggestion-modal-card .v-label,
.user-suggestion-modal-card input,
.user-suggestion-modal-card textarea,
.user-suggestion-modal-card .v-input input,
.user-suggestion-modal-card .v-input textarea,
.user-suggestion-modal-card .v-text-field input,
.user-suggestion-modal-card .v-textarea textarea,
.user-suggestion-modal-card .v-input__slot input,
.user-suggestion-modal-card .v-input__slot textarea {
    color: black;
}

/* Placeholder color override and font size */
.user-suggestion-modal-card input::placeholder,
.user-suggestion-modal-card textarea::placeholder,
.user-suggestion-modal-card .v-input input::placeholder,
.user-suggestion-modal-card .v-input textarea::placeholder,
.user-suggestion-modal-card .v-text-field input::placeholder,
.user-suggestion-modal-card .v-textarea textarea::placeholder {
    color: grey;
    font-size: 1rem;
}

/* Button font size */
.user-suggestion-modal-card .v-card__actions .v-btn,
.user-suggestion-modal-card .v-card__actions .v-btn span {
    font-size: 1rem;
}

.submit-btn-layout {
    flex: 1;
    min-width: 0;
}

/* Override Vuetify dark button background for submit button */
.custom-disabled-btn.submit-btn-normal.theme--dark.v-btn.v-btn--has-bg {
    background-color: rgb(59, 130, 246);
}

.custom-disabled-btn.submit-btn-loading.theme--dark.v-btn.v-btn--has-bg {
    background-color: grey !important;
    color: white !important;
}

.snackbar-margin {
    margin-bottom: 32px;
}

@media (max-width: 600px) {
    .black-text {
        font-size: 1rem;
    }
    .user-suggestion-modal-card input::placeholder,
    .user-suggestion-modal-card textarea::placeholder,
    .user-suggestion-modal-card .v-input input::placeholder,
    .user-suggestion-modal-card .v-input textarea::placeholder,
    .user-suggestion-modal-card .v-text-field input::placeholder,
    .user-suggestion-modal-card .v-textarea textarea::placeholder {
        font-size: 0.8rem;
    }
    .user-suggestion-modal-card .v-card__actions .v-btn,
    .user-suggestion-modal-card .v-card__actions .v-btn span {
        font-size: 0.6rem;
        font-weight: bold;
    }
}
</style>